import type { CirclesEngineInput } from '@shared/engine/circles/core';
import { OSU_PLAYFIELD_HEIGHT, OSU_PLAYFIELD_WIDTH } from '@shared/circlesChart';
import { circlesTapCodes, loadKeybindPrefs } from '../keybindPrefs';
import { computeCanvasPlayfield } from './circlesPlayfieldGeometry';

export interface CirclesInputSource {
  readonly kind: 'pointer' | 'replay';
  poll: (currentBeat: number) => CirclesEngineInput[];
  destroy: () => void;
}

/**
 * Project (clientX, clientY) into osu playfield space using the same
 * canvas geometry the renderer uses. Returns the projected coords plus
 * an `inField` flag that's false when the click landed in the canvas's
 * letterbox margins (or anywhere outside the canvas entirely).
 *
 * The (x, y) returned is always clamped to `[0, 512] × [0, 384]` so
 * callers that care only about cursor position (e.g. drawing the
 * cursor) can ignore `inField`. Callers that care about whether the
 * click should match a circle (the tap-queue path) MUST honour
 * `inField` — otherwise a click in the letterbox snaps to an osu edge
 * and can satisfy the radius check for a near-edge circle, granting a
 * free hit from a click that landed outside the visible playfield.
 *
 * Critical: this MUST share view dimensions with the renderer
 * (`computeCanvasPlayfield` floors view width/height to the same min
 * the renderer does). At sub-floor viewports a divergence would land
 * taps on a different osu coordinate than the circle the player sees.
 */
export function clientToPlayfield(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
): { x: number; y: number; inField: boolean } {
  const { rect, playfield } = computeCanvasPlayfield(canvas);
  const localX = clientX - rect.left - playfield.x;
  const localY = clientY - rect.top - playfield.y;
  const x = (localX / playfield.w) * OSU_PLAYFIELD_WIDTH;
  const y = (localY / playfield.h) * OSU_PLAYFIELD_HEIGHT;
  const inField = x >= 0 && x <= OSU_PLAYFIELD_WIDTH && y >= 0 && y <= OSU_PLAYFIELD_HEIGHT;
  return {
    x: Math.max(0, Math.min(OSU_PLAYFIELD_WIDTH, x)),
    y: Math.max(0, Math.min(OSU_PLAYFIELD_HEIGHT, y)),
    inField,
  };
}

/**
 * Pointer-driven Circles input source. Listens on the canvas (and
 * window for keyboard fallbacks) and queues `(beat, x, y)` taps each
 * time the player clicks / taps inside the playfield.
 *
 * Keyboard tap fallback: pressing one of the configured tap keys
 * registers a tap at the most recent cursor position. The bound set
 * is the union of the user's customisable Circles tap keys (default
 * Z / X), Space (always-on convention), and every Piano lane key —
 * so a player who muscle-memorises Q W E R from Piano can also use
 * them as tap keys in Circles. Bindings are read once at construction
 * via {@link loadKeybindPrefs}; rebinds mid-session take effect after
 * the next session restart.
 *
 * Press tracking: the source maintains a held-surfaces set toggled
 * by pointer-down/up and keydown/keyup for each bound code. The
 * renderer uses it to enlarge the cursor reticle while a tap surface
 * is held — pure cosmetic feedback, doesn't influence engine behaviour.
 */

export class PointerInputSource implements CirclesInputSource {
  readonly kind = 'pointer' as const;
  private queue: CirclesEngineInput[] = [];
  private getBeatAtNow: () => number;
  private isClockRunning: () => boolean;
  private canvas: HTMLCanvasElement;
  private active = true;
  private lastCursorPosition: { x: number; y: number } | null = null;
  private tapKeyCodes: ReadonlySet<string>;
  /**
   * Set of currently-held tap surfaces (pointer button ids + key codes).
   * Cursor reads `pressed = pressedSurfaces.size > 0`. A set rather
   * than a boolean so concurrent inputs (mouse held + Z key tapped
   * mid-hold) don't release the cursor pulse early when one of them
   * lifts.
   */
  private pressedSurfaces = new Set<string>();

  constructor(
    canvas: HTMLCanvasElement,
    getBeatAtNow: () => number,
    isClockRunning: () => boolean = () => true,
  ) {
    this.canvas = canvas;
    this.getBeatAtNow = getBeatAtNow;
    this.isClockRunning = isClockRunning;
    this.tapKeyCodes = circlesTapCodes(loadKeybindPrefs());
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
    canvas.addEventListener('pointerleave', this.onPointerUp);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onWindowBlur);
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    const pf = clientToPlayfield(this.canvas, e.clientX, e.clientY);
    // Cursor tracking always uses the clamped (x, y) so the rendered
    // cursor stays inside the playfield even when the mouse drifts
    // into the letterbox. The `inField` flag is only consulted by the
    // tap-queue path below.
    this.lastCursorPosition = { x: pf.x, y: pf.y };
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 2) {
      return;
    }
    e.preventDefault();
    // Always mark the pointer as held even when the click lands in
    // the letterbox or the clock is paused — the cursor visual cue
    // still wants to reflect "I'm holding the mouse button" so the
    // player gets immediate physical feedback.
    this.pressedSurfaces.add(`pointer-${e.pointerId}`);
    if (!this.isClockRunning()) {
      return;
    }
    const pf = clientToPlayfield(this.canvas, e.clientX, e.clientY);
    this.lastCursorPosition = { x: pf.x, y: pf.y };
    if (!pf.inField) {
      // Click landed in the canvas's letterbox margin. Drop it rather
      // than queueing a clamped-edge tap that could satisfy the radius
      // check for an edge circle (free hit from an off-field click).
      return;
    }
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, x: pf.x, y: pf.y });
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    this.pressedSurfaces.delete(`pointer-${e.pointerId}`);
  };

  private onContextMenu = (e: MouseEvent): void => {
    if (!this.active) {
      return;
    }
    if (!this.isClockRunning()) {
      return;
    }
    // Right-click is a second tap surface during play; suppress the
    // context menu only when the click landed on the canvas, so other
    // page chrome retains normal context-menu behaviour.
    e.preventDefault();
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    // osu!-style keyboard tap at last cursor position. The bound set
    // is read from keybind prefs and includes Space + circles tap
    // keys + every piano lane key, so a player can use Q W E R as
    // tap surfaces here without learning a second binding scheme.
    if (!this.tapKeyCodes.has(e.code)) {
      return;
    }
    // Track held state before the `repeat` gate so the cursor visual
    // stays enlarged for the entire keypress, not just the initial
    // tap — auto-repeat events hit this path while the key is down.
    this.pressedSurfaces.add(`key-${e.code}`);
    if (e.repeat) {
      return;
    }
    e.preventDefault();
    if (!this.isClockRunning()) {
      return;
    }
    const pf = this.lastCursorPosition ?? {
      x: OSU_PLAYFIELD_WIDTH / 2,
      y: OSU_PLAYFIELD_HEIGHT / 2,
    };
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, x: pf.x, y: pf.y });
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    if (!this.tapKeyCodes.has(e.code)) {
      return;
    }
    this.pressedSurfaces.delete(`key-${e.code}`);
  };

  private onWindowBlur = (): void => {
    // Clear all held state on blur — the OS swallows keyup events
    // when focus leaves the page, so without this the cursor would
    // stay enlarged after the player alt-tabs away mid-tap.
    this.pressedSurfaces.clear();
  };

  poll(_currentBeat: number): CirclesEngineInput[] {
    if (this.queue.length === 0) {
      return [];
    }
    const out = this.queue;
    this.queue = [];
    return out;
  }

  /** Most recent pointer / keyboard cursor position (for the renderer). */
  getCursorPosition(): { x: number; y: number } | null {
    return this.lastCursorPosition;
  }

  /**
   * Whether at least one tap surface (pointer button or tap key) is
   * currently held. Renderer uses it to enlarge the cursor reticle
   * while held, returning to default size on release.
   */
  isCursorPressed(): boolean {
    return this.pressedSurfaces.size > 0;
  }

  destroy(): void {
    this.active = false;
    this.pressedSurfaces.clear();
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('pointerleave', this.onPointerUp);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onWindowBlur);
  }
}

/**
 * Replay input source for headless / verifier flows. Mirrors
 * {@link import('../input/inputSource.js').ReplayInputSource}.
 */
export class CirclesReplayInputSource implements CirclesInputSource {
  readonly kind = 'replay' as const;
  private inputs: CirclesEngineInput[];
  private cursor = 0;

  constructor(inputs: CirclesEngineInput[]) {
    this.inputs = [...inputs].sort((a, b) => a.beat - b.beat);
  }

  poll(currentBeat: number): CirclesEngineInput[] {
    const out: CirclesEngineInput[] = [];
    while (this.cursor < this.inputs.length && this.inputs[this.cursor].beat <= currentBeat) {
      out.push(this.inputs[this.cursor]);
      this.cursor += 1;
    }
    return out;
  }

  destroy(): void {
    this.inputs = [];
    this.cursor = 0;
  }

  isExhausted(): boolean {
    return this.cursor >= this.inputs.length;
  }
}
